import React from 'react';
import clsx from 'clsx';

/** Roughly a logo and six lines — past that a signature is not a signature. */
const defaultMaxHeight = 320;

/** Enough for one line, so an empty signature is still a visible frame. */
const minHeight = 40;

/**
 * Held until the first measurement lands, and kept if one never does. It is the
 * one state in which the preview can still scroll.
 */
const fallbackHeight = 96;

/** The frame's own breathing room, before any correction for tight type. */
const basePadding = 8;

/**
 * The document the preview frames.
 *
 * Padding rather than a body margin: a margin collapses with the first child's
 * and escapes to the root, so the room it was meant to give the first line ends
 * up outside the frame. `max-width` keeps a logo saved at its natural width
 * from putting a horizontal scrollbar across the frame, and `overflow-anchor`
 * is off because scroll anchoring answers a document growing as images decode
 * by moving the scroll offset.
 *
 * The canvas is white whatever the app's theme is doing. A signature is
 * authored against the white background a mail client gives it and carries its
 * own colours for that, so dark ink on a dark frame is the one pairing it can
 * never survive. `color-scheme: light` states the same thing to the engine:
 * `prefers-color-scheme` reads the root's used scheme, so a signature shipping
 * a dark-mode override stays on the branch it was designed in rather than
 * turning its text white against this white.
 */
const previewDocument = (bodyHtml: string) =>
  `<!doctype html><html><head><meta charset="utf-8"><style>` +
  `html{color-scheme:light;background:#fff}` +
  `html,body{margin:0;padding:0}` +
  `body{padding:${basePadding}px;overflow-anchor:none;overflow-wrap:anywhere}` +
  `img,table,video{max-width:100%}img{height:auto}` +
  `</style></head><body>${bodyHtml}</body></html>`;

/**
 * How far the signature's glyphs reach above the boxes layout gave them.
 *
 * A line-height tighter than the font size — routine in signatures pasted out
 * of a mail client — leaves the ascenders outside their line box. A scroll
 * container's scrollable area never extends above its own origin, so that ink
 * is clipped and no amount of extra frame height reaches it; the only cure is
 * to start the content lower down. The overshoot is a function of the
 * signature's own type, so it is measured rather than guessed at.
 */
export const signatureInkAboveOrigin = (doc: Document): number => {
  const view = doc.defaultView;
  if (!view) return 0;
  // Measured from the content box rather than the frame, so the answer does not
  // change once the correction it asks for has been applied.
  const bodyStyle = view.getComputedStyle(doc.body);
  const contentTop = doc.body.getBoundingClientRect().top + (parseFloat(bodyStyle.paddingTop) || 0);
  let highest = 0;
  for (const element of Array.from(doc.body.querySelectorAll('*'))) {
    const style = view.getComputedStyle(element);
    const fontSize = parseFloat(style.fontSize) || 0;
    const lineHeight =
      style.lineHeight === 'normal' ? fontSize * 1.2 : parseFloat(style.lineHeight) || 0;
    // Half the leading sits above the text, and is negative when the line box
    // is shorter than the type it carries.
    const inkTop = element.getBoundingClientRect().top + (lineHeight - fontSize) / 2;
    highest = Math.min(highest, inkTop - contentTop);
  }
  return Math.ceil(Math.max(0, -highest));
};

/**
 * Renders stored mail HTML — a signature, or a whole outgoing message — the way
 * a mail client will, in an iframe that can do nothing else, grown to fit its
 * content so nothing needs scrolling to read.
 *
 * Nothing sanitizes the stored markup, so this frames other people's raw HTML.
 * What makes that safe is the token the sandbox does not carry: with no
 * `allow-scripts` the document cannot execute anything. `allow-same-origin`
 * buys exactly one thing — an opaque origin makes contentDocument unreadable,
 * and without reading it the page cannot learn how tall the signature is.
 * Never pair the two: together they let the framed document reach its own frame
 * element through window.parent and take the sandbox attribute off itself.
 */
export const SignaturePreview = ({
  bodyHtml,
  title = 'Email signature preview',
  maxHeight = defaultMaxHeight,
  bordered = true,
  className
}: {
  bodyHtml: string;
  /** Named per signature where several could be on screen at once. */
  title?: string;
  /**
   * Where the frame stops growing and starts scrolling, or null to let it grow
   * to whatever it holds. Pass null wherever the surrounding layout scrolls
   * already, so the reader is not given two bars over one block of content.
   */
  maxHeight?: number | null;
  /**
   * Draws the frame's own border. Off where the signature sits inside a card
   * that already encloses it, so the two rules do not stack.
   */
  bordered?: boolean;
  className?: string;
}) => {
  const frame = React.useRef<HTMLIFrameElement | null>(null);
  const observer = React.useRef<ResizeObserver | null>(null);
  const [height, setHeight] = React.useState<number | null>(null);

  const fit = React.useCallback(() => {
    const doc = frame.current?.contentDocument;
    if (!doc?.documentElement || !doc.body) return;

    // Measured against the stylesheet's own padding so the correction is the
    // same on every pass and the resize observer settles instead of creeping.
    const padding = basePadding + signatureInkAboveOrigin(doc);
    if (doc.body.style.paddingTop !== `${padding}px`) {
      doc.body.style.paddingTop = `${padding}px`;
    }

    // Measured on the body, not the document element, whose scrollHeight is
    // floored at the viewport: sizing the frame to that answer makes the next
    // measurement report the size just set, and the frame grows a pixel a pass.
    // The extra pixel absorbs sub-pixel rounding, which would otherwise leave a
    // scrollbar on a frame that is one fraction of a pixel too short.
    const content =
      Math.max(doc.body.scrollHeight, Math.ceil(doc.body.getBoundingClientRect().height)) + 1;

    // The frame is a border-box, so its own border eats into the height set on
    // it: sized to the content alone it is one hairline short at each edge, and
    // a signature that fits still scrolls.
    const border = (frame.current?.offsetHeight ?? 0) - (frame.current?.clientHeight ?? 0);
    setHeight(Math.min(maxHeight ?? Infinity, Math.max(minHeight, content)) + border);
  }, [maxHeight]);

  // An image that decodes after load changes the height the frame was measured
  // at, and the frame runs no scripts to report it, so the page watches the
  // document that allow-same-origin lets it read.
  const onLoad = React.useCallback(() => {
    fit();
    observer.current?.disconnect();
    const body = frame.current?.contentDocument?.body;
    if (!body || typeof ResizeObserver === 'undefined') return;
    observer.current = new ResizeObserver(fit);
    observer.current.observe(body);
  }, [fit]);

  React.useEffect(() => () => observer.current?.disconnect(), []);

  return (
    <iframe
      ref={frame}
      title={title}
      sandbox="allow-same-origin"
      onLoad={onLoad}
      style={{ height: height ?? fallbackHeight }}
      className={clsx(
        // Matches the document's own background, so the frame shows no dark
        // band in the moment before the srcDoc paints.
        'w-full bg-white',
        bordered && 'rounded-md border border-neutral-200 dark:border-neutral-700',
        className
      )}
      srcDoc={previewDocument(bodyHtml)}
    />
  );
};
